import { useEffect, useRef, useState } from 'react';
import { AlertCircle, RefreshCw } from 'lucide-react';
import { quizzesApi } from '../../lib/api';
import type { VideoStyle } from '../../types/quizzes';
import { Button, Spinner } from '../ui';

interface PlayerGlobal {
  mount?: (el: HTMLElement, opts: { config: unknown; preview: true }) => unknown;
}

declare global {
  interface Window {
    MyLeadSyncerVideo?: PlayerGlobal & Record<string, unknown>;
  }
}

let playerPromise: Promise<PlayerGlobal> | null = null;

/** Load `/v/player.js` once per page. A failed load can be retried. */
function loadPlayer(): Promise<PlayerGlobal> {
  if (window.MyLeadSyncerVideo?.mount) return Promise.resolve(window.MyLeadSyncerVideo);
  if (playerPromise) return playerPromise;
  playerPromise = new Promise<PlayerGlobal>((resolve, reject) => {
    const script = document.createElement('script');
    script.src = quizzesApi.getVideoPlayerScriptUrl();
    script.async = true;
    script.dataset.mlsBuilder = '1';
    script.onload = () => {
      if (window.MyLeadSyncerVideo?.mount) resolve(window.MyLeadSyncerVideo);
      else reject(new Error('The player script loaded but did not expose MyLeadSyncerVideo.mount'));
    };
    script.onerror = () => reject(new Error('The player script could not be loaded'));
    document.head.appendChild(script);
  }).catch((err) => {
    playerPromise = null;
    throw err;
  });
  return playerPromise;
}

/** Whatever mount() returns: an unmount function, or an object with destroy()/unmount(). */
function teardown(handle: unknown, el: HTMLElement | null) {
  try {
    if (typeof handle === 'function') (handle as () => void)();
    else if (handle && typeof handle === 'object') {
      const h = handle as { destroy?: () => void; unmount?: () => void };
      if (typeof h.destroy === 'function') h.destroy();
      else if (typeof h.unmount === 'function') h.unmount();
    }
  } catch { /* player teardown is best-effort */ }
  if (el) el.replaceChildren();
}

/**
 * Renders the real player in preview mode (no events are sent).
 *
 * The config comes from GET /api/quizzes/:id/video/preview-config, re-fetched
 * whenever `refreshKey` changes (bump it after a save). `styleOverride` lets the
 * Style tab show unsaved theme edits on top of the saved config.
 */
export default function VideoPlayerPreview({
  quizId, refreshKey, styleOverride,
}: {
  quizId: string;
  refreshKey: number;
  styleOverride?: VideoStyle;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [config, setConfig] = useState<Record<string, unknown> | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [retry, setRetry] = useState(0);

  // Fetch the preview config.
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    quizzesApi.getVideoPreviewConfig(quizId).then((res) => {
      if (cancelled) return;
      if (res.success && res.data) {
        // Accept `{ data: config }` or `{ data: { config } }`.
        const d = res.data as Record<string, unknown>;
        setConfig((d.config as Record<string, unknown>) ?? d);
      } else {
        setConfig(null);
        setError(res.message || 'Could not load the preview. Add a video and save first.');
      }
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, [quizId, refreshKey, retry]);

  // Debounce the live style so typing in a field doesn't remount on every keystroke.
  const styleJson = styleOverride ? JSON.stringify(styleOverride) : '';
  const [style, setStyle] = useState<VideoStyle | undefined>(styleOverride);
  useEffect(() => {
    const t = setTimeout(() => setStyle(styleJson ? (JSON.parse(styleJson) as VideoStyle) : undefined), 400);
    return () => clearTimeout(t);
  }, [styleJson]);

  // Mount the player.
  useEffect(() => {
    if (!config) return;
    const el = containerRef.current;
    if (!el) return;
    let handle: unknown = null;
    let cancelled = false;
    loadPlayer()
      .then((player) => {
        if (cancelled || !player.mount) return;
        const merged = style ? { ...config, style } : config;
        el.replaceChildren();
        handle = player.mount(el, { config: merged, preview: true });
      })
      .catch((err: Error) => { if (!cancelled) setError(`${err.message}. The live preview is unavailable, but you can still edit and save.`); });
    return () => { cancelled = true; teardown(handle, el); };
  }, [config, style]);

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm font-medium text-ink-2">Live preview</span>
        <button type="button" onClick={() => setRetry((n) => n + 1)} className="flex items-center gap-1 text-xs text-ink-3 hover:text-ink">
          <RefreshCw className="w-3.5 h-3.5" /> Reload
        </button>
      </div>
      <div className="border border-line rounded-lg bg-surface-2 overflow-hidden min-h-[220px] relative">
        {loading && <div className="absolute inset-0 flex items-center justify-center"><Spinner /></div>}
        {error && !loading && (
          <div className="p-4 text-sm text-ink-3 flex items-start gap-2">
            <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0 text-warning" />
            <div>
              <p className="m-0">{error}</p>
              <Button variant="ghost" className="mt-2" onClick={() => setRetry((n) => n + 1)}>Try again</Button>
            </div>
          </div>
        )}
        <div ref={containerRef} className={error ? 'hidden' : ''} />
      </div>
      <p className="text-xs text-ink-3 mt-1">Preview mode: nothing is recorded or synced. Question and video changes show after you save.</p>
    </div>
  );
}
